import BluetoothIcon from '@assets/icons/bluetooth.svg';
import CheckmarkIcon from '@assets/icons/checkmark.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from './activity-indicator';
import {
  BluetoothDeviceListOption,
  useBluetoothContext,
} from '@/contexts/bluetooth-context';
import { captureException } from '@/utils/sentry';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { List, Props as ListProps } from './list';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  ItemSeparatorComponent?: ListProps<BluetoothDeviceListOption>['ItemSeparatorComponent'];
  listItemStyle?: StyleProp<ViewStyle>;
  onSelect?: () => void;
}

export const PrinterSelectList: React.FC<Props> = ({
  containerStyle,
  ItemSeparatorComponent,
  listItemStyle,
  onSelect,
}) => {
  const { showErrorToast } = useToasts();
  const [styles, theme] = useStyles();
  const { getPairedDevices, hasPermission, selectDevice, selectedDevice } =
    useBluetoothContext();

  const [listOptions, setListOptions] = useState<BluetoothDeviceListOption[]>(
    [],
  );
  const [loadingAddress, setLoadingAddress] = useState<string | null>(null);

  useEffect(() => {
    const getAndSetList = async () => {
      try {
        const pairedDevices = await getPairedDevices();
        setListOptions(pairedDevices);
      } catch (e) {
        captureException(e);
        showErrorToast();
      }
    };
    if (hasPermission) {
      getAndSetList();
    }
  }, [getPairedDevices, hasPermission, showErrorToast]);

  return listOptions.length > 0 ? (
    <List
      contentContainerStyle={containerStyle}
      data={listOptions}
      ItemSeparatorComponent={ItemSeparatorComponent}
      listItemStyle={listItemStyle}
      onSelect={async (option) => {
        if (loadingAddress) {
          return;
        }
        try {
          setLoadingAddress(option.metadata!.device.address);
          if (await selectDevice(option.metadata!.device)) {
            onSelect?.();
            return;
          }
        } catch (e) {
          captureException(e);
        }

        showErrorToast();
        setLoadingAddress(null);
      }}
      renderLeftComponent={() => (
        <BluetoothIcon stroke={theme.sk.color.icon.primary} />
      )}
      renderRightComponent={({ metadata }) => {
        const address = metadata?.device.address;
        return address === selectedDevice?.address ? (
          <View style={styles.selectedIcon}>
            <CheckmarkIcon
              height={20}
              stroke={theme.sk.color.icon.primary}
              testID="selected-printer-icon"
              width={20}
            />
          </View>
        ) : address === loadingAddress ? (
          <ActivityIndicator size="small" />
        ) : null;
      }}
    />
  ) : (
    <Text
      style={[TYPOGRAPHY.medium, FONTS.primarySemiBold, styles.emptyStateText]}
    >
      {i18n.t('no_printers_detected')}
    </Text>
  );
};

const useStyles = createStyles(() => ({
  emptyStateText: {
    alignSelf: 'center',
    fontSize: 18,
    marginHorizontal: 24,
    marginVertical: 24,
    textAlign: 'center',
  },
  selectedIcon: {
    marginRight: 8,
  },
}));
